/**
 * R7 WS-1 §4–§4.6 — every derivation behind screens 10 and 11, as PURE functions.
 *
 * # ⛔ Why all of it is here and none of it is in the panes
 *
 * This is where the correctness lives. The panels' whole job is *not out-claiming their producers*
 * (§4.1–§4.5), and each rule is a branch: truncated `orgNames`, an empty sparkline that is not a
 * flat zero, a status outside the remedy map, a dpId with no rate card. A pure function is testable
 * without a render, which is what makes 100% branch coverage of that absence matrix affordable —
 * and it means the rules are stated once rather than at each of the two screens that need them.
 * Same split R6 used (`fleet-model.ts`, `rollout-model.ts`, `history-model.ts`).
 *
 * # ⛔ EVERY LOOKUP MAP HERE IS NULL-PROTOTYPE AND FROZEN
 *
 * Both `DpIssue.status` and `OrganizationSummary.status` are typed **`string`** on the producer, so
 * every map in this file is indexed by a value nobody enumerated. On an ordinary object literal
 * `REMEDY['toString']` returns an **inherited function** — truthy, so a `?? fallback` never fires and
 * the caller is handed `Function.prototype.toString`. That is R6 security finding S-2 exactly, and
 * the repo has already ruled on this axis twice: `SectionRail.tsx:63` (`ICONS`) and
 * `contracts/src/grant-principal.ts:40-49`. ⛔ Do not "simplify" any of these back to a bare literal.
 */
import type { CostSummary } from '@zion/sdk-contracts';
import { BILLING_READ_PERMISSION } from '../api/billing.api';
import type { DashboardOverview, DpIssue, SparklinePoint } from '../api/dashboard.api';
import type { DataPlaneSummary, DpLiveStatus } from '../api/dataPlanes.api';
import type { RootShellHostsByPlacement, TenantListItem, TenantPlacementBrief } from '../api/tenants.api';
import { isForbidden, type AsyncState } from './async-state';
import type { ChannelChain } from './channel-chain';
/* `dataPlaneRoute` holds the data-plane route and its safe-id allowlist once (R2, R12). */
import { dataPlaneRoute } from './dp-tabs';
import { preferredRootShellHost } from './root-shell-hosts-model';

/**
 * Freeze a lookup table onto a null prototype. See this file's header for why. Exported for
 * `inflight-model.ts`, whose keys are server strings too.
 *
 * ⛔ The RETURN type is `V | undefined`, deliberately. `noUncheckedIndexedAccess` is off in this
 * repo (`tsconfig.base.json`), so a plain `Record<string, V>` would type a miss as `V` — making
 * every `?? fallback` in this file look like dead code to a reader and to the compiler, which is
 * how the fallback gets deleted. The signature states the truth: indexing with a string nobody
 * enumerated can come back empty.
 */
export function closedMap<V>(source: Readonly<Record<string, V>>): Readonly<Record<string, V | undefined>> {
  return Object.freeze(Object.assign(Object.create(null) as Record<string, V>, source));
}

// ─────────────────────────────────────────────────────────────────────────────
// §4.1 — the `Needs attention` panel
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ⛔ Whether the panel has rows, is genuinely empty, or is reporting a CONTRADICTION (A7).
 *
 * `allHealthy` is a producer field (`dashboard.service.ts:217,224,281`), not a derivation from
 * `issues.length`, so the two can disagree. `issues: []` with `allHealthy: false` says *"the fleet is
 * not healthy and here is nothing"* — that is not an all-clear, and rendering it as `EmptyState`
 * would report the one thing the operator opened the screen to rule out.
 *
 * ⚠️ The plan names two of the four combinations. The other mismatch — rows present with
 * `allHealthy: true` — is the same defect from the other side and gets the same treatment, because
 * the alternative is a fourth arm that has to pick one field to believe.
 */
export type AttentionPanelState = 'rows' | 'empty' | 'contradiction';

export function attentionPanelState(issues: readonly DpIssue[], allHealthy: boolean): AttentionPanelState {
  if (issues.length > 0) return allHealthy ? 'contradiction' : 'rows';
  return allHealthy ? 'empty' : 'contradiction';
}

/**
 * The affected-tenant cell (A6). ⛔ `orgNames` is `.slice(0, 3)` on the producer
 * (`dashboard.service.ts:250`) while `orgCount` is the true count (`:268`). Rendering the names alone
 * silently under-reports, so the count travels with them and the overflow is named.
 */
export interface AffectedTenants {
  readonly names: readonly string[];
  /** The producer's true count. */
  readonly count: number;
  /** `count − names.length`, floored at 0. `0` ⇒ no "+N more" affordance. */
  readonly moreCount: number;
}

export function affectedTenants(issue: DpIssue): AffectedTenants {
  // ⛔ `Math.max(0, …)`: a producer that ever reported `orgCount < orgNames.length` would otherwise
  // render "+-1 more". Clamping is not hiding it — the names and the count are both still on screen.
  return {
    names: issue.orgNames,
    count: issue.orgCount,
    moreCount: Math.max(0, issue.orgCount - issue.orgNames.length),
  };
}

/**
 * §4.2 — the remedy link. **A CLOSED MAP over the three statuses E1 can emit**, and no fallback.
 *
 * The deck's four rows carry hand-authored remedies (*"Add capacity"*, *"Repin channel"*, *"Set config
 * key"*, *"Alert override"*) composed from five other screens' data. E1 produces none of that, so
 * these are the honest form: the one screen that can act on the condition.
 *
 * ⛔ **A `default:` / `??` arm is FORBIDDEN** (A8). `DpIssue.status` is typed `string`
 * (`dashboard.service.ts:49`), not the three-value union the service filters to
 * (`:212-214`), so a fourth status is reachable — and a fallback would hand it a plausible **wrong**
 * remedy. A lookup miss renders the row with its status text and **no button**.
 */
const REMEDY_LABEL = closedMap<string>({
  failed: 'Diagnose',
  disabled: 'Open data plane',
  draining: 'Open data plane',
});

export interface Remedy {
  readonly label: string;
  /** Basename-relative, from {@link dataPlaneRoute}, which refuses an unsafe id (R12). */
  readonly to: string;
}

/** The tenant detail route — screen 11. ⚠️ The uuid is the segment, never the slug. */
export function tenantRoute(tenantId: string): string {
  return `/platform/tenants/${encodeURIComponent(tenantId)}`;
}

/** One DNS label: lowercase letters, digits and inner hyphens, 63 characters at most. */
const DNS_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/** The address of the app host a tenant opens, as an href and the host it names. */
export interface TenantAppUrl {
  readonly href: string;
  readonly host: string;
}

/**
 * The tenant's app address, for the title-row link of the tenant drill-in. Three sources, in order:
 *
 * 1. The first verified and bound custom domain in `rootShellHosts` (E22).
 * 2. Else the org's canonical host in `rootShellHosts`.
 * 3. Else the data plane host, `<dp name>.<base domain>`.
 *
 * An unplaced tenant gets no link. Only the E22 row of the shown placement counts, so a held answer
 * from before a Migrate cannot name the old data plane. `preferredRootShellHost` picks the first two
 * sources and refuses a host outside the DNS grammar. Pass `null` for `rootShellHosts` while E22 has
 * no answer or failed; the data plane host then decides. Returns `null` when no source gives a safe
 * host. Pure.
 */
export function tenantAppUrl(
  placement: { readonly id: string; readonly name: string } | null,
  dataPlanes: readonly Pick<DataPlaneSummary, 'id' | 'baseDomain'>[],
  rootShellHosts: RootShellHostsByPlacement | null,
): TenantAppUrl | null {
  if (placement === null) return null;
  const onPlacement =
    rootShellHosts === null
      ? null
      : { placements: rootShellHosts.placements.filter((row) => row.dataPlaneId === placement.id) };
  const preferred = onPlacement === null ? null : preferredRootShellHost(onPlacement);
  if (preferred !== null) return { href: new URL(`https://${preferred}/`).href, host: preferred };
  if (!DNS_LABEL.test(placement.name)) return null;
  const base = dataPlanes.find((dp) => dp.id === placement.id)?.baseDomain ?? null;
  if (base === null) return null;
  const labels = base.split('.');
  if (labels.length < 2 || !labels.every((label) => DNS_LABEL.test(label))) return null;
  const host = `${placement.name}.${base}`;
  return { href: new URL(`https://${host}/`).href, host };
}

/** `undefined` ⇒ render the status and NO button. See {@link REMEDY_LABEL}. */
export function remedyFor(issue: DpIssue): Remedy | undefined {
  const label = REMEDY_LABEL[issue.status];
  if (label === undefined) return undefined;
  return { label, to: dataPlaneRoute(issue.id) };
}

/**
 * The sparkline cell (A9).
 *
 * ⛔ `[]` is **`notcollected`**, NEVER a flat zero line. `queryErrorRateSparkline` returns `[]` on any
 * failure and on an empty result alike (`dashboard.service.ts:347-370`), and a zero error-rate line
 * reads as *perfectly healthy* when it means *we could not ask*. That is the single most dangerous
 * shape on a triage screen.
 */
export type SparklineCell =
  | { readonly kind: 'series'; readonly points: readonly SparklinePoint[] }
  | { readonly kind: 'notcollected' };

export function sparklineCell(issue: DpIssue): SparklineCell {
  return issue.sparkline.length === 0
    ? { kind: 'notcollected' }
    : { kind: 'series', points: issue.sparkline };
}

/**
 * §4.1 / #825 — the three concerns that can never appear, named on screen **unconditionally** (A10).
 *
 * ⛔ The line renders whether or not a chip appeared, because a chip for `error_rate` says nothing
 * about the other three: `cpu`, `memory` and `restarts` query metric names that exist in no Mimir
 * tenant, every query's failure is swallowed, and `extractScalar` returns `0` for an empty result.
 * ⇒ "no cpu chip" means *nothing was measured*, not *cpu is fine*.
 */
export const UNMEASURED_CONCERNS: readonly string[] = ['cpu', 'memory', 'restarts'];

export const UNMEASURED_REASON =
  'CPU, memory and container restarts are not measured for any data plane. A missing chip for ' +
  'them means nothing was measured, not that they are healthy. Error rate is measured.';

/** Every concern the producer's union admits, so a new one is a typecheck event, not a blank chip. */
export const CONCERN_LABEL: Readonly<Record<DpIssue['hotspots'][number]['concern'], string>> = {
  cpu: 'CPU',
  memory: 'Memory',
  sql: 'SQL',
  nats: 'NATS',
  error_rate: 'Error rate',
  restarts: 'Restarts',
};

// ─────────────────────────────────────────────────────────────────────────────
// The bounded fan-outs (§4.3, §4.5)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The result of a per-DP or per-org fan-out — the fulfilled subset **plus the ids that failed**.
 *
 * ⛔ `Promise.allSettled`, never `Promise.all`: one 500 must not blank the panel. ⛔ And never a
 * SILENT partial — an unreported failure count is the "did not look = found nothing" collapse, so
 * `failedIds` is a required field rather than something a caller may ignore, and A16 asserts the
 * count reaches the screen.
 */
export interface FanOut<T> {
  readonly ok: ReadonlyMap<string, T>;
  readonly failedIds: readonly string[];
}

/**
 * Zip ids with their settled results.
 *
 * ⚠️ `ids` and `results` are positional partners: the caller must pass the same array it mapped over.
 * A length mismatch would silently mis-attribute a result to the wrong id, so it throws rather than
 * truncating — a wrong DP's staleness rendered under another DP's name is a plausible-looking lie.
 */
export function collectFanOut<T>(
  ids: readonly string[],
  results: readonly PromiseSettledResult<T>[],
): FanOut<T> {
  if (ids.length !== results.length) {
    throw new Error(`collectFanOut: ${ids.length} ids but ${results.length} results`);
  }
  const ok = new Map<string, T>();
  const failedIds: string[] = [];
  for (const [index, id] of ids.entries()) {
    const result = results[index];
    if (result.status === 'fulfilled') ok.set(id, result.value);
    else failedIds.push(id);
  }
  return { ok, failedIds };
}

// ─────────────────────────────────────────────────────────────────────────────
// §4.3 — the cannot-assess band
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One DP whose report we cannot trust.
 *
 * ⛔ **NOT styled as an alert** at the render site: staleness is a gap in OUR knowledge, not a fault
 * in the tenant, which is the whole reason this band exists separately from `Needs attention`.
 *
 * `ageSeconds` is the FACT (computed from `reportedAt`); `stale` / `unresponsive` are the SERVER'S
 * VERDICT (`dp-admin.service.ts:140-141`). ⛔ The verdict is never recomputed from the age — a second
 * threshold in the client disagrees with the server's the day either moves.
 */
export interface CannotAssessRow {
  readonly dpId: string;
  readonly name: string;
  /** `null` ⇒ `reportedAt` did not parse; the row renders the raw value via {@link age}. */
  readonly ageSeconds: number | null;
  /** The human form, already resolved so the renderer holds no date logic. */
  readonly age: string;
  readonly stale: boolean;
  readonly unresponsive: boolean;
}

/** Included iff `operatorStale || operatorUnresponsive` — the server's own answer. */
export function cannotAssessRows(
  dps: readonly DataPlaneSummary[],
  liveStatus: FanOut<DpLiveStatus>,
  nowMs: number,
): readonly CannotAssessRow[] {
  const out: CannotAssessRow[] = [];
  for (const dp of dps) {
    const live = liveStatus.ok.get(dp.id);
    if (live === undefined) continue;
    if (!live.operatorStale && !live.operatorUnresponsive) continue;
    out.push({
      dpId: dp.id,
      name: dp.name,
      ageSeconds: ageSeconds(live.reportedAt, nowMs),
      age: formatAge(live.reportedAt, nowMs),
      stale: live.operatorStale,
      unresponsive: live.operatorUnresponsive,
    });
  }
  return out;
}

/**
 * Age of a report, in whole seconds. `nowMs` is INJECTED so tests are deterministic.
 *
 * ⛔ Returns `null` for an unparseable timestamp rather than `0` or `NaN`: `0` would render as
 * "just now" — a confident wrong answer about the freshest possible data, on the panel whose entire
 * subject is staleness.
 */
export function ageSeconds(iso: string, nowMs: number): number | null {
  const parsed = Date.parse(iso);
  // ⛔ `null`, not `NaN`: `NaN` propagates silently through arithmetic and formats as "NaNs ago",
  // whereas `null` forces every consumer to decide what to render.
  if (Number.isNaN(parsed)) return null;
  // ⛔ Floored at 0: clock skew between the reporter and this browser can put `reportedAt` in the
  // future, and a negative age renders as "-3 minutes ago".
  return Math.max(0, Math.floor((nowMs - parsed) / 1000));
}

/**
 * A coarse, human age. ⛔ Never an em dash and never "just now" for an unparseable input — the raw
 * value is what someone pastes into a bug report (same rule as `format.ts`'s `formatTimestamp`).
 */
export function formatAge(iso: string, nowMs: number): string {
  const seconds = ageSeconds(iso, nowMs);
  if (seconds === null) return iso;
  if (seconds < 90) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 90) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ─────────────────────────────────────────────────────────────────────────────
// §4.4 — the fleet counts behind the status line
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The fleet counts the status line and the attention heading read.
 *
 * `null` in gives `null` out: a count of an unresolved set is not zero. While E1 was in flight the old
 * strip rendered "Need attention: 0", which is the answer the operator hoped for and nothing measured.
 * There is no `total − Σ(counters)` field: E2's `total` is `allDps.length` and is not the sum of its
 * seven status counters, so the residual is no category.
 */
export interface FleetStrip {
  /** `null` ⇒ E1 has not resolved. ⛔ NEVER `0` — see this function's docblock. */
  readonly needAttention: number | null;
  /** `null` ⇒ the per-DP `live-status` fan-out has not resolved. */
  readonly cannotAssess: number | null;
  /** `null` ⇒ E3 has not resolved. */
  readonly tenantsPlaced: number | null;
  readonly tenantsTotal: number | null;
}

export function fleetStrip(
  issues: readonly DpIssue[] | null,
  cannotAssess: readonly CannotAssessRow[] | null,
  tenants: readonly TenantListItem[] | null,
): FleetStrip {
  return {
    needAttention: issues === null ? null : issues.length,
    cannotAssess: cannotAssess === null ? null : cannotAssess.length,
    tenantsPlaced: tenants === null ? null : tenants.filter((t) => t.placements.length > 0).length,
    tenantsTotal: tenants === null ? null : tenants.length,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// §4.5 — the `All organisations` table
// ─────────────────────────────────────────────────────────────────────────────

/** Badge tones. `neutral` is the honest tone for a status nobody enumerated. */
export type BadgeTone = 'positive' | 'warning' | 'critical' | 'neutral';

/**
 * Org status → tone. ⛔ CLOSED, with an explicit unknown arm (A13).
 *
 * `OrganizationSummary.status` is typed `string` (`org-admin.service.ts:30`). A
 * `default: 'positive'` would paint an unknown state green — the worst available default, because
 * green is the one tone an operator does not look at twice.
 */
const ORG_STATUS_TONE = closedMap<BadgeTone>({
  active: 'positive',
  suspended: 'warning',
  decommissioned: 'critical',
});

export function statusBadgeTone(status: string): BadgeTone {
  return ORG_STATUS_TONE[status] ?? 'neutral';
}

// ─────────────────────────────────────────────────────────────────────────────
// Owner bug list 2026-08-05 — hide the dead, but never hide what still runs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * # The org lifecycle vocabulary, and the terminal set DERIVED from it
 *
 * > *"on the tenants page, we should NOT show decommissioned tenants … unless they are consuming
 * > resources for some reason but we should be explicit"*
 *
 * ⛔ **READ FROM THE PRODUCER, NOT INFERRED FROM THE ROUTES.**
 * `TENANT_STATUSES = ['active','suspended','decommissioned'] as const` —
 * `platform-api/src/modules/org/dto/internal-tenant.dto.ts:19` — consumed as `z.enum(TENANT_STATUSES)`
 * by `internalTenantSchema` and as the cast at `internal-org.service.ts:201`. Enumerating the three
 * *verbs* (`suspend`/`unsuspend`/`decommission`) would have produced the same list by luck; deriving a
 * vocabulary from the routes that write it is what took the Releases tab down.
 *
 * ⚠️ And the wire type is WIDER than this list: the column is `text('status').default('active')`
 * (`organizations.ts:32`) with no pg enum, and site-admin-api types the field `string`
 * (`org-admin.service.ts:30`). So an unenumerated value is reachable, and {@link tenantVisibility}
 * fails **open** on one.
 */
export const ORG_LIFECYCLE_STATUSES = ['active', 'suspended', 'decommissioned'] as const;

export type OrgLifecycleStatus = (typeof ORG_LIFECYCLE_STATUSES)[number];

/**
 * ⭐⭐ **HOW A STATUS IS LISTED — R-31 / R-14c. This vocabulary REPLACED `OrgLifecycleClass`.**
 *
 * ## ⚠️ What was here before, and why replacing the vocabulary beat flipping a value
 *
 * This was `OrgLifecycleClass = 'live' | 'terminal'`, with `decommissioned: 'terminal'`, and it drove
 * three things: `TERMINAL_ORG_STATUSES`, a `hiddenByDefault` filter, and the copy that told the
 * operator *"this cannot be reversed"*. **Every one of those became false when the restore route
 * landed** (`POST /v1/orgs/:id/restore`, `platform-api`'s `OrgService.restore`).
 *
 * ⛔ **The tempting minimal change was `decommissioned: 'terminal' → 'live'`. ESC-2 ruled against it
 * (arm (a), R-31), and the reason is worth keeping:** the *word* `terminal` would then classify
 * nothing — every status would be `live`, the type would have one reachable member, and
 * `TERMINAL_ORG_STATUSES` would derive to `[]`, making `hiddenByDefault` permanently `false` and both
 * of `tenantVisibility`'s branches unreachable. A vocabulary whose distinction has evaporated is worse
 * than a renamed one: it keeps a reader looking for a difference that is no longer there.
 *
 * ⇒ the classification now describes what it actually decides — **how a row is LISTED** — and nothing
 * is hidden by default at all.
 *
 * ⚠️ **`suspended` sits with `active`, still on purpose, and it is the mistake worth naming.**
 * `unsuspend` (`internal-org.service.ts`) returns a suspended org to `active`, so hiding one would hide
 * the tenant an operator is most likely to be chasing.
 */
export type OrgListingPolicy = 'plain' | 'note-if-placed';

/**
 * ⛔ EXHAUSTIVE BY CONSTRUCTION. `Record<OrgLifecycleStatus, …>` means adding a member to
 * {@link ORG_LIFECYCLE_STATUSES} without classifying it here fails to compile. ⛔ Do not replace this
 * with a lookup carrying a default arm. Same shape as `site-admin-api`'s `dp-lifecycle.ts`, which
 * solves the identical problem for the DP vocabulary — deliberately NOT the same list: this is the
 * ORG vocabulary, a different column on a different table.
 *
 * ⭐ **This paragraph is the one thing kept verbatim from the deleted map**, because the property it
 * protects is unchanged and is the reason the replacement is a `Record` rather than a lookup.
 */
const ORG_LISTING_POLICY: Record<OrgLifecycleStatus, OrgListingPolicy> = {
  active: 'plain',
  suspended: 'plain',
  decommissioned: 'note-if-placed',
};

/**
 * ⛔ **THE SAME MAP, READ THROUGH A TYPE THAT ADMITS THE FREE-TEXT KEY — and the pair is the point.**
 *
 * {@link ORG_LISTING_POLICY} is `Record<OrgLifecycleStatus, …>` so a new member of
 * {@link ORG_LIFECYCLE_STATUSES} is a **compile error** until it is classified. But `tenant.status` is
 * a `string` off the wire (the column is free text with no pg enum), so indexing that exact type with
 * it does not compile either — and the two requirements pull in opposite directions.
 *
 * ⇒ one map, two views: the **write** view is total, the **read** view says `| undefined` out loud.
 *
 * ⛔ **Do NOT collapse these into one `Record<string, OrgListingPolicy>`.** That is the shape the plan's
 * *"do not replace this with a lookup carrying a default arm"* rule forbids: it would accept a new
 * status silently and lose the compile-time exhaustiveness that is the whole reason the map is a
 * `Record`. ⛔ And do not reach for a cast at the call site instead — the `| undefined` here is what
 * documents the fail-open behaviour in the types rather than only in a comment.
 */
const ORG_LISTING_POLICY_BY_STATUS: Readonly<Record<string, OrgListingPolicy | undefined>> =
  ORG_LISTING_POLICY;

/**
 * Why a terminal tenant is still on screen.
 *
 * ⛔ **IT CLAIMS PLACEMENT, NOT SPEND, AND THE LINE BETWEEN THEM IS THE WHOLE POINT.** The owner asked
 * for the resource-consuming exception, and this is the strongest claim the data supports:
 * `CostSummary` buckets by `byResourceType` / `byDataPlane` / `byApp` — **there is no `byOrg`**
 * (`contracts/src/billing.ts:229-241`) — and one data plane can host several orgs, so no per-tenant
 * figure is derivable and no DP's existence is attributable to one tenant. Saying *"still placed on a
 * running data plane"* is provable; saying *"costing you $X"* would be a confident filter built on a
 * signal that does not exist, which is the failure mode the P0 record warns about.
 */
export const STILL_PLACED_NOTE =
  /*
   * ⚠️ Sentence 1 lost its trailing clause — it read *"… — so it is listed rather than hidden."* That
   * contrast is false once nothing is hidden (R-31 deleted the hide-by-default filter), and a sentence
   * that explains a mechanism the reader cannot observe reads as a bug in the screen.
   * ⛔ **Sentences 2–4 are VERBATIM** — each is a measured claim (decommissioning removes no data
   * plane; per-tenant spend is not derivable because `CostSummary` has no `byOrg`), and none of them
   * changed. The docblock's *"IT CLAIMS PLACEMENT, NOT SPEND"* argument is untouched too.
   */
  'Decommissioned, but still placed on a running data plane. ' +
  'Nothing about decommissioning a tenant stops or removes its data plane. Per-tenant spend is not ' +
  'shown because the billing summary is bucketed by data plane, not by tenant, and a data plane can ' +
  'serve more than one.';

/**
 * ⛔ **WHAT DECOMMISSIONING AN ORGANISATION ACTUALLY DOES — every line measured from the service.**
 *
 * Rendered in the confirmation (Platform-5: a destructive action must say what it does). ⛔ Every claim
 * here was read out of the producer, because writing reassuring copy nobody verified is how a console
 * teaches an operator something false about their own fleet.
 *
 * `OrgService.decommission` (`platform-api/src/modules/org/org.service.ts:183-207`) sets
 * `organizations.status='decommissioned'`, bumps `updatedAt`, and writes one `org.decommission` audit
 * row. **That is the entire operation.**
 *
 * - ⛔ It never touches `org_placements` or `data_placements`, and `provisioning-svc/src` references
 *   `platform.organizations` nowhere ⇒ **no data plane is stopped, drained or destroyed.**
 * - ⛔ Neither `platform-api`'s metering nor `site-admin-api`'s `billing-admin` filters on org status
 *   ⇒ **metered spend continues.**
 * - ⭐ **REVERSIBLE, IN TWO DELIBERATE STEPS — R-14c.** `suspend` and `unsuspend` still throw
 *   `BadRequestException` on a decommissioned org (all four guards are byte-identical, deliberately),
 *   but `OrgService.restore` (`POST /v1/orgs/:id/restore`, `cp:tenant:delete`) now moves it to
 *   **`suspended`** — and a separate `unsuspend` then returns it to `active`.
 * - It does stop org signing-key rotation (`rotation.service.ts:90`) and asset reconciliation
 *   (`org-assets-reconciler.ts:66`), both of which select `status='active'`.
 *
 * ## ⚠️ THIS DOCBLOCK USED TO SAY "⛔ IRREVERSIBLE", AND THE CORRECTION IS NOT COSMETIC
 *
 * It read: *"`suspend` and `unsuspend` both throw … and no writer anywhere moves it to another status.
 * There is no reactivate route,"* plus *"⛔ And no 'contact support to restore' — no such path was
 * found, and offering an unverified escape hatch on an irreversible action is worse than offering
 * none."* **Both were true when measured and both are now false**, and the second is the dangerous one:
 * it instructed future authors *not* to offer a restore path, so leaving it would argue against the
 * feature that now exists, in the file that models it.
 *
 * ⛔ **"Irreversible" ALONE WOULD HAVE BEEN MISLEADING even then**, which is why the data-plane line is
 * still not optional: an operator told only "this cannot be undone" reasonably concludes the
 * infrastructure goes with it, and would then stop looking for the running data plane they are still
 * paying for. ⭐ That argument survives the reversal intact — it was never about irreversibility.
 */
export const DECOMMISSION_CONSEQUENCES: readonly string[] = [
  /*
   * ⛔ Entry 0 rewritten (R-14c). It said *"This cannot be reversed — there is no route back to
   * active"*. ⚠️ The replacement keeps the half that is STILL TRUE — Suspend and Unsuspend do both
   * refuse a decommissioned org — because that is the fact an operator hits if they try the obvious
   * thing, and deleting it would make the two-step look like an arbitrary ceremony.
   */
  'The organisation is marked decommissioned. Suspend and Unsuspend both refuse a decommissioned organisation — reversing this takes two deliberate steps: Restore returns it to suspended, and a separate Unsuspend then returns it to active.',
  // ⛔ Entries 1–3 VERBATIM. Each was measured off the service and none of them changed.
  'Its data plane is NOT stopped, drained or destroyed, and its placement is not removed. Anything running keeps running.',
  'Metered spend continues. Nothing about this action stops billing.',
  'Organisation signing-key rotation and asset reconciliation stop, because both act only on active organisations.',
];

/**
 * ⭐ **WHAT RESTORING AN ORGANISATION ACTUALLY DOES — R-14c. Every line measured off
 * `OrgService.restore`, not written to reassure.**
 *
 * Rendered in the **typed** confirmation (Platform-5: a destructive action must say what it does — and
 * a status change on a tenant with live infrastructure is one, even when the direction is "undo").
 *
 * ⛔ **Line 1 is the one that must never be softened.** The single most likely operator misreading of a
 * button labelled *Restore* is *"the tenant is back"*. It is not: it lands on `suspended`, and six
 * consumers gate on `status = 'active'` — org signing-key rotation, asset reconciliation, and four
 * `cloud/dp/core-api` readers (recovery, NATS entitlement ×2, notify). Saying so is what stops an
 * operator walking away believing the tenant is serving traffic again.
 */
export const RESTORE_CONSEQUENCES: readonly string[] = [
  'The organisation is moved from decommissioned to suspended — not to active. It stays unusable until a separate Unsuspend.',
  'Signing-key rotation and asset reconciliation stay stopped, because both act only on active organisations.',
  'Nothing about its data plane or its placement changes — neither was touched by decommissioning.',
  'The suspension carries no date and no reason, because this was not a suspension. The audit log records who restored it and when.',
];

/**
 * ⭐ **What one tenant row needs to say about itself — R-31.**
 *
 * ## ⛔ NOTHING IS HIDDEN ANY MORE, AND THAT IS THE CHANGE
 *
 * This used to answer *"does this row survive the default view?"*, and the rule was *"hide terminal
 * states that are INERT; surface the ones that are not, and label them"*. ⛔ **The `terminal` half of
 * that rule is gone with the vocabulary** — a decommissioned org is restorable, so there is no inert
 * terminal state left to hide. Every tenant is listed; the only remaining question is whether a row
 * needs the {@link STILL_PLACED_NOTE} annotation.
 *
 * ⚠️ **The measurement that made the note necessary is unchanged and still the point.** `decommission`
 * sets `organizations.status`, bumps `updatedAt`, and writes one audit row. **That is all.** It never
 * touches `org_placements` or `data_placements`, and `provisioning-svc` references
 * `platform.organizations` nowhere, so nothing tears a data plane down in response. ⇒ a decommissioned
 * org KEEPS its `placements[]` — and that array is already producer-filtered to live DPs
 * (`org-admin.service.ts:105,159-161`), so a placement here *is* a running data plane.
 */
export interface TenantVisibility {
  /**
   * Decommissioned **and** still on a live data plane ⇒ {@link STILL_PLACED_NOTE} renders.
   *
   * ⚠️ The interface lost `terminal` and `hiddenByDefault`. Neither was renamed: `terminal` described a
   * vocabulary that no longer exists, and `hiddenByDefault` described a filter that was deleted.
   */
  readonly stillPlaced: boolean;
}

/**
 * ⭐ **Both operands stay reachable, which is exactly why the vocabulary was replaced rather than the
 * value flipped:** `decommissioned`+placed ⇒ `true`; `decommissioned`+unplaced ⇒ `false`;
 * `active`+placed ⇒ `false`. Flipping `decommissioned` to `live` would have made the left operand
 * constant and both branches dead.
 *
 * ⚠️ **IT FAILS OPEN, AND THAT IS DELIBERATE — same direction as the `IS_TERMINAL_ORG_STATUS` it
 * replaces.** `tenant.status` is a **free-text** column with no pg enum (`organizations.ts:32`;
 * site-admin-api types it `string`), so `ORG_LISTING_POLICY[tenant.status]` on an unrecognised value is
 * `undefined`, which is `!== 'note-if-placed'` ⇒ `stillPlaced: false` ⇒ the row is **listed and
 * unannotated**. A spurious missing annotation is cosmetic; a silently hidden tenant would be
 * operational. Asserted, not assumed.
 *
 * ⚠️ ⛔ **A prototype key is safe here too, but for a different reason than before.** The old code used
 * a null-prototype `closedMap` because `TERMINAL['toString']` would otherwise be an inherited
 * *function* — truthy — and the row would read terminal. `ORG_LISTING_POLICY` is an ordinary object
 * literal, so `ORG_LISTING_POLICY['toString']` IS that inherited function — but the comparison is
 * `=== 'note-if-placed'`, which a function fails. ⇒ fail-open, the same safe direction.
 * ⛔ **Do not "harden" this into a truthiness check** (`if (ORG_LISTING_POLICY[status])`), which would
 * flip a prototype key to annotated and reintroduce the exact hazard `closedMap` existed for.
 */
export function tenantVisibility(tenant: TenantListItem): TenantVisibility {
  return {
    stillPlaced:
      ORG_LISTING_POLICY_BY_STATUS[tenant.status] === 'note-if-placed' &&
      tenant.placements.length > 0,
  };
}

/**
 * ⛔ **"EMPTY BECAUSE YOU FILTERED EVERYTHING OUT" ≠ "THERE IS NOTHING HERE."**
 *
 * Required rather than polish: RULE #2b makes the filter STICKY, so a filter state that hides every
 * row persists across sessions and **never self-heals**. Naming the two cases apart is the only way
 * out of that trap — the operator has to be able to tell "I did this" from "the platform is empty".
 */
export type TableEmptiness = 'has-rows' | 'nothing-here' | 'all-filtered';

export function tableEmptiness(totalCount: number, visibleCount: number): TableEmptiness {
  if (visibleCount > 0) return 'has-rows';
  return totalCount === 0 ? 'nothing-here' : 'all-filtered';
}

// ─────────────────────────────────────────────────────────────────────────────
// RULE #2b — sorting
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The columns this table can be ordered by.
 *
 * ⛔ **Only columns that are ACTUALLY orderable.** *Data plane*, *Release channel*, *versions*, *Last
 * report* and *Spend* are all **per-placement** — a tenant can hold several, so there is no single
 * value to sort a tenant row by, and a "sort by spend" that silently ordered on `placements[0]` would
 * be a confident wrong answer on the money column. ⛔ They are therefore not offered at all rather
 * than offered and quietly wrong. (See §4.6: a column of `1`s quietly starts truncating.)
 */
export const TENANT_SORT_KEYS = ['name', 'status', 'createdAt'] as const;

export type TenantSortKey = (typeof TENANT_SORT_KEYS)[number];

/**
 * Compare two tenants on one column. ⛔ **THE ONE collation in the product, and now the ONLY sorter.**
 *
 * ## ⛔⛔ `sortTenants` WAS DELETED, AND ITS REMOVAL FIXED A MUTATION SHIELD
 *
 * `TenantsPane` used to call `sortTenants` to pre-order rows and *then* hand them to a `ScrollTable`
 * that sorted again. Both were seeded from the same view state, so they always agreed and the redundancy
 * read as belt-and-braces. ⛔ **It was a mutation shield:** `sortRows` is specified stable, so rows
 * arriving already ordered kept that order, and replacing the table's `column.compare` with a naive
 * `sortValue` — deleting the status→name tie-break outright — **reded ZERO tests**. Every column
 * comparator in that pane was decorative and no amount of coverage could say so.
 *
 * ⇒ ⭐ **A redundant sort is not belt-and-braces; it is a shield that makes the thing downstream of it
 * unfalsifiable.** One sorter: the table calls this, via `column.compare`.
 *
 * ⚠️ Exported for that reason — `ScrollTable.column.compare` needs `(a, b) => number`, and a caller that
 * cannot reach this would have to re-implement the collation, which is the divergence this exists to stop.
 *
 * ⛔ `localeCompare` for the text columns, NOT `<`/`>`. Raw comparison orders by UTF-16 code unit, so
 * every capitalised name sorts before every lowercase one — `Zeta` before `acme` — which reads as
 * broken sorting rather than as a collation choice. ⛔ And `createdAt` compares as an ISO STRING on
 * purpose: ISO-8601 with a fixed offset is lexicographically ordered, so no `Date` is constructed and
 * an unparseable value cannot become `NaN` and land in an arbitrary position.
 */
export function compareTenants(a: TenantListItem, b: TenantListItem, key: TenantSortKey): number {
  if (key === 'name') return a.name.localeCompare(b.name);
  if (key === 'status') {
    // Ties inside one status fall back to the name, so the order is TOTAL — an unstable tail looks
    // like rows jumping at random on re-render.
    const byStatus = a.status.localeCompare(b.status);
    return byStatus === 0 ? a.name.localeCompare(b.name) : byStatus;
  }
  const byCreated = a.createdAt.localeCompare(b.createdAt);
  return byCreated === 0 ? a.name.localeCompare(b.name) : byCreated;
}

/** DP lifecycle status → tone, for the placement/phase chips. Same closed-map rule. */
const DP_STATUS_TONE = closedMap<BadgeTone>({
  active: 'positive',
  ready: 'positive',
  creating: 'warning',
  draining: 'warning',
  disabled: 'warning',
  failed: 'critical',
  destroyed: 'neutral',
});

export function dpStatusBadgeTone(status: string): BadgeTone {
  return DP_STATUS_TONE[status] ?? 'neutral';
}

/**
 * The Spend cell. It has five kinds, and none of them is `$0` (§3.1.4 / A11):
 *
 * - `amount`: the DP's cost from the summary.
 * - `notcollected`: the read failed (`null`), or the DP is not in the summary ("no rate card"). The
 *   absence of a price is not a price of zero. Each case has its own reason (R7-DEC-7).
 * - `forbidden`: the reader lacks `cp:billing:read` and the pane sent no read (I1323). It renders as
 *   "no access".
 * - `errorstate`: a reader who holds the grant got a 403. It renders as "refused", as the tenant
 *   drill-in Spend card does.
 * - `pending`: the read is in flight. It renders as `checking…` and claims no failure (D6).
 *
 * ⛔ `cost` stays the WIRE string here — a decimal string by contract (`billingDecimalStringSchema`),
 * and there is no arithmetic anywhere in this file. The view rounds it to two decimals for display
 * through `formatCostWithCode` in `billing-money.tsx`.
 */
export type SpendCell =
  | { readonly kind: 'amount'; readonly cost: string; readonly currency: string }
  | { readonly kind: 'notcollected'; readonly reason: string }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'errorstate'; readonly reason: string }
  | { readonly kind: 'pending' };

/**
 * What the pane knows of the billing summary: the summary, `'forbidden'` when the reader lacks
 * `cp:billing:read` and the pane sent no read, `'refused'` when a reader with the permission got a
 * 403 answer, `'loading'` while the read is in flight, and `null` after another failure.
 */
export type SpendSource = CostSummary | 'forbidden' | 'refused' | 'loading' | null;

export const NO_RATE_CARD_REASON =
  'no rate card configured — the absence of a price, not a price of zero.';
export const SPEND_UNREADABLE_REASON =
  'the billing summary could not be read, so this period’s spend is unknown.';
export const SPEND_FORBIDDEN_REASON = `you need ${BILLING_READ_PERMISSION} to read this period’s spend.`;
export const SPEND_REFUSED_REASON = 'the server refused the billing read, so this period’s spend is unknown.';

/**
 * The spend source of the Tenants list, from the reader's grant and the billing read.
 * - `canReadBilling` false: `'forbidden'`, whatever the state (the pane sent no read).
 * - a 403 answer: `'refused'`. Any other failure: `null`.
 * - `loading`, or `ready` with no summary yet: `'loading'`. A summary: the summary.
 */
export function spendSourceOf(canReadBilling: boolean, billing: AsyncState<CostSummary | null>): SpendSource {
  if (!canReadBilling) return 'forbidden';
  if (isForbidden(billing)) return 'refused';
  if (billing.kind === 'error') return null;
  if (billing.kind === 'loading' || billing.data === null) return 'loading';
  return billing.data;
}

/**
 * `summary === null` ⇒ E13 failed; `'loading'` ⇒ it is in flight and claims no failure. `'forbidden'` is a denial and
 * `'refused'` is a server refusal (`errorstate`); neither is "not collected" (I1323). `dpId` is the placement's DP.
 */
export function spendForDp(summary: SpendSource, dpId: string): SpendCell {
  if (summary === 'forbidden') return { kind: 'forbidden', reason: SPEND_FORBIDDEN_REASON };
  if (summary === 'refused') return { kind: 'errorstate', reason: SPEND_REFUSED_REASON };
  if (summary === 'loading') return { kind: 'pending' };
  if (summary === null) return { kind: 'notcollected', reason: SPEND_UNREADABLE_REASON };
  const bucket = summary.byDataPlane.find((b) => b.key === dpId);
  if (bucket === undefined) return { kind: 'notcollected', reason: NO_RATE_CARD_REASON };
  return { kind: 'amount', cost: bucket.cost, currency: summary.currency };
}

/**
 * The version pair for one placement.
 *
 * ⛔ **`known: false` and `desired: null` are DIFFERENT FACTS and must not collapse.** `known: false`
 * means the placement's dpId was absent from E10 — we cannot state the versions at all. `desired:
 * null` with `known: true` means the DP is in the list and has no desired version, which is the real
 * *"never delivered"* state. One is an absence of measurement, the other is a measured absence.
 */
export interface VersionPair {
  readonly known: boolean;
  readonly desired: string | null;
  readonly running: string | null;
}

/** The Last-report cell. `notchecked` ⇒ this DP's `live-status` call rejected (A16). */
export type ReportCell =
  | {
      readonly kind: 'reported';
      readonly reportedAt: string;
      readonly stale: boolean;
      readonly unresponsive: boolean;
    }
  | { readonly kind: 'notchecked' };

/** One row of the Data-plane / versions / report / spend group, per placement. */
export interface PlacementRow {
  readonly dpId: string;
  readonly name: string;
  readonly phase: string;
  readonly versions: VersionPair;
  readonly report: ReportCell;
  readonly spend: SpendCell;
}

/**
 * One table row.
 *
 * ⛔ `placements` is an ARRAY and `[]` is a real state (§4.6). The data model permits many orgs per
 * DP and many DPs per org; in practice every DP runs exactly one org — but a modelled capability is
 * not an as-built fact, and a column of `1`s quietly starts truncating.
 *
 * ⛔ `channel` is `null` when the E14/E15 pair REJECTED for this org ⇒ the cell renders
 * `notcollected` *"channel could not be resolved"*. ⛔ Never blank and ⛔ never `stable` (A17): a
 * defaulted `stable` is indistinguishable from a real one.
 */
export interface TenantRow {
  readonly tenant: TenantListItem;
  readonly to: string;
  readonly stateTone: BadgeTone;
  readonly placements: readonly PlacementRow[];
  readonly channel: ChannelChain | null;
}

export interface TenantTableInput {
  readonly tenants: readonly TenantListItem[];
  readonly dps: readonly DataPlaneSummary[];
  readonly liveStatus: FanOut<DpLiveStatus>;
  /**
   * The billing summary, or: `'loading'` while E13 is in flight, `'forbidden'` when the reader lacks
   * the grant, `'refused'` for a 403 to a holder, and `null` for a failure that is not a 403.
   */
  readonly spend: SpendSource;
  /** orgId → chain. An ABSENT key means the pair rejected — see {@link TenantRow.channel}. */
  readonly channels: ReadonlyMap<string, ChannelChain>;
}

function placementRow(
  placement: TenantPlacementBrief,
  dpsById: ReadonlyMap<string, DataPlaneSummary>,
  input: TenantTableInput,
): PlacementRow {
  const dp = dpsById.get(placement.dpId);
  const live = input.liveStatus.ok.get(placement.dpId);
  return {
    dpId: placement.dpId,
    name: placement.name,
    phase: placement.phase,
    versions:
      dp === undefined
        ? { known: false, desired: null, running: null }
        : { known: true, desired: dp.desiredVersion, running: dp.currentVersion },
    report:
      live === undefined
        ? { kind: 'notchecked' }
        : {
            kind: 'reported',
            reportedAt: live.reportedAt,
            stale: live.operatorStale,
            unresponsive: live.operatorUnresponsive,
          },
    spend: spendForDp(input.spend, placement.dpId),
  };
}

export function tenantTableRows(input: TenantTableInput): readonly TenantRow[] {
  const dpsById = new Map(input.dps.map((dp) => [dp.id, dp]));
  return input.tenants.map((tenant) => ({
    tenant,
    to: tenantRoute(tenant.id),
    stateTone: statusBadgeTone(tenant.status),
    placements: tenant.placements.map((p) => placementRow(p, dpsById, input)),
    channel: input.channels.get(tenant.id) ?? null,
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// The fleet status line and the attention heading
// ─────────────────────────────────────────────────────────────────────────────

/** `1 data plane` or `N data planes`. */
function dataPlanes(n: number): string {
  return `${n} data plane${n === 1 ? '' : 's'}`;
}

function needAttention(n: number): string {
  return `${dataPlanes(n)} need${n === 1 ? 's' : ''} attention`;
}

/** One clause of the status line. `key` is stable per clause, so the renderer can key on it. */
export interface FleetStatusSegment {
  readonly key: 'health' | 'overview' | 'tenants';
  readonly text: string;
}

/** The status line: its clauses in reading order, and the tone of its leading dot. */
export interface FleetStatus {
  readonly tone: BadgeTone;
  readonly segments: readonly FleetStatusSegment[];
}

/** The E2 data-plane counters in the producer's order. `total` is not their sum, so it is printed apart. */
const OVERVIEW_STATES = ['active', 'ready', 'creating', 'failed', 'disabled', 'draining', 'destroyed'] as const;

function healthSegment(
  health: AsyncState<{ readonly issues: readonly DpIssue[]; readonly allHealthy: boolean }>,
  unchecked: number | null,
): { readonly tone: BadgeTone; readonly text: string } {
  if (health.kind === 'loading') return { tone: 'neutral', text: 'Checking the fleet…' };
  if (health.kind === 'error') return { tone: 'neutral', text: 'Fleet health unknown' };
  const state = attentionPanelState(health.data.issues, health.data.allHealthy);
  // A self-contradicting response claims nothing; the body renders the ErrorState that says why.
  if (state === 'contradiction') return { tone: 'neutral', text: 'Fleet health unknown' };
  // D16: the all-clear needs every data plane checked; an unknown count is not zero.
  if (state === 'empty') {
    return unchecked === 0
      ? { tone: 'positive', text: 'Fleet healthy' }
      : { tone: 'neutral', text: 'No issues reported' };
  }
  return { tone: 'warning', text: needAttention(health.data.issues.length) };
}

/**
 * The status line under the Tenants heading, as data so the copy is tested without a render.
 *
 * `health` is E1. `overview` is E2, `null` until it resolves; while it is `null` the overview clause is absent.
 * `strip` supplies the tenant counts; a `null` count means E3 is unresolved and the tenants clause is
 * absent, never zero. The overview clause prints the total first and only the non-zero states.
 *
 * `unchecked` counts the data planes whose health could not be checked: server-stale rows plus failed
 * live-status reads, `null` until live status is read. "Fleet healthy" with the `positive` tone is
 * printed only when E1 reports no issue and `unchecked` is 0 (D16); with no issue and any other
 * `unchecked` it prints "No issues reported" in `neutral`. `positive` is produced by no other clause.
 */
export function fleetStatusSegments(
  health: AsyncState<{ readonly issues: readonly DpIssue[]; readonly allHealthy: boolean }>,
  overview: DashboardOverview | null,
  strip: FleetStrip,
  unchecked: number | null,
): FleetStatus {
  const head = healthSegment(health, unchecked);
  const segments: FleetStatusSegment[] = [{ key: 'health', text: head.text }];
  if (overview !== null) {
    const counts = overview.dataPlanes;
    const parts = OVERVIEW_STATES.flatMap((state) => (counts[state] > 0 ? [`${counts[state]} ${state}`] : []));
    const total = dataPlanes(counts.total);
    segments.push({ key: 'overview', text: parts.length === 0 ? total : `${total}: ${parts.join(', ')}` });
  }
  if (strip.tenantsPlaced !== null && strip.tenantsTotal !== null) {
    const noun = strip.tenantsTotal === 1 ? 'tenant' : 'tenants';
    segments.push({ key: 'tenants', text: `${strip.tenantsPlaced} of ${strip.tenantsTotal} ${noun} placed` });
  }
  return { tone: head.tone, segments };
}

/**
 * The attention list's heading: `2 data planes need attention`, `1 could not be checked`, or both
 * joined with ` · `. `null` when both counts are zero, because the list is then absent.
 */
export function attentionHeading(issueCount: number, cannotAssessCount: number): string | null {
  const parts: string[] = [];
  if (issueCount > 0) parts.push(needAttention(issueCount));
  if (cannotAssessCount > 0) parts.push(`${cannotAssessCount} could not be checked`);
  return parts.length === 0 ? null : parts.join(' · ');
}
